import type { Object3D } from 'three';
import type { ObjectSnapshot } from '../../scene-diff-manager';
import type { TransitionableObjectType, TransitionContext } from '../../../types/engine.types';
import type { SceneTransitionManager } from '../scene-transition-manager.class';
import { prepareMaterialsForFadeCfn, restoreMaterialsCfn, setObjectOpacityCfn } from './material-state.cfn';

/**
 * Animate object removals with fade out and scale down
 */
export const animateRemovalsCfn = async (
	self: SceneTransitionManager,
	objects: ObjectSnapshot[]
): Promise<void> => {
	if (objects.length === 0) return;

	const config = self.config;
	const { fadeOutDuration, fadeOutScale, animationManager } = config;
	const promises: Promise<void>[] = [];

	for (const snapshot of objects) {
		const { object } = snapshot;
		if (!object || !object.parent) continue;

		let foundInScene = false;
		let current: Object3D | null = object;
		while (current) {
			if (current.type === 'Scene') {
				foundInScene = true;
				break;
			}
			current = current.parent;
		}
		if (!foundInScene) continue;

		const animId = self.nextAnimationId('removal');
		const { parent } = object;

		prepareMaterialsForFadeCfn(self, object);

		const fadePromise = animationManager.animateOpacity(
			`${animId}-opacity`,
			1,
			0,
			fadeOutDuration,
			(opacity) => setObjectOpacityCfn(object, opacity)
		);

		// Relative to the object's own scale: an appliance group is scaled ×10
		// around its 0.1 model, and setting it to 1 would snap it to a tenth.
		const baseScale = object.scale.clone();
		const scalePromise = animationManager.animateScale(
			`${animId}-scale`,
			1,
			fadeOutScale,
			fadeOutDuration,
			(scale) => object.scale.copy(baseScale).multiplyScalar(scale)
		);

		const removalPromise = Promise.all([fadePromise, scalePromise]).then(() => {
			if (object.parent === parent) {
				parent.remove(object);
			}
			restoreMaterialsCfn(self, object);
		});

		promises.push(removalPromise);
	}

	await Promise.all(promises);
};

/**
 * Animate a single removal by type using transition registry
 */
export const animateRemovalByTypeCfn = async (
	self: SceneTransitionManager,
	snapshot: ObjectSnapshot
): Promise<void> => {
	const transitionRegistry = self.transitionRegistry;
	const config = self.config;

	const type = snapshot.type as TransitionableObjectType;
	const typeConfig = transitionRegistry[type]?.remove;

	if (!typeConfig?.helper || !snapshot.object) return;

	const context: TransitionContext = {
		object: snapshot.object,
		metadata: {
			id: snapshot.id,
			type: snapshot.type as TransitionableObjectType
		},
		animationManager: config.animationManager,
		duration: typeConfig.duration
	};

	const result = typeConfig.helper(context);
	await result.promise;

	if (snapshot.object.parent) {
		snapshot.object.parent.remove(snapshot.object);
	}
};

/**
 * Process removals by type in reverse order
 */
export const processRemovalsByTypeCfn = async (
	self: SceneTransitionManager,
	removed: ObjectSnapshot[]
): Promise<void> => {
	const islandSequence = self.islandSequence;
	const { typeOrder } = islandSequence;
	const reversedOrder = [...typeOrder].reverse();

	for (const type of reversedOrder) {
		const objectsOfType = removed.filter((obj) => obj.type === type);
		if (objectsOfType.length === 0) continue;

		const promises = objectsOfType.map((obj) => animateRemovalByTypeCfn(self, obj));
		await Promise.all(promises);
	}

	const remainingTypes = removed.filter((obj) => !typeOrder.includes(obj.type as TransitionableObjectType));
	if (remainingTypes.length > 0) {
		const promises = remainingTypes.map((obj) => animateRemovalByTypeCfn(self, obj));
		await Promise.all(promises);
	}
};
